import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Users, Gift, ShareNetwork } from "@phosphor-icons/react";

/**
 * ReferFriendsCard — Dashboard block that shows the current user's referral
 * link + stats + copy/share controls. Only rendered when Site Config has
 * `referrals_enabled=true`, otherwise the whole feature is invisible.
 *
 * Business rule (spec 2026-07-10):
 *   Every user their referral link earns THEM +5 recipe slots each time a
 *   referred user adds a first recipe. Capped at 15 qualifying signups
 *   (= 75 bonus slots). Referrals beyond the cap still count toward the
 *   leaderboard shown in Admin, they just don't grant additional slots.
 */
export default function ReferFriendsCard() {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        if (alive) setState({ loading: false, data });
      } catch (e) {
        if (alive) setState({ loading: false, data: null });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <div className="bg-gradient-to-br from-rf-amber/25 to-rf-cream-alt rounded-3xl p-6 border border-rf-amber/40 animate-pulse" data-testid="refer-friends-loading">
        <div className="h-6 w-40 bg-rf-ink/10 rounded mb-3" />
        <div className="h-4 w-64 bg-rf-ink/10 rounded" />
      </div>
    );
  }
  if (!state.data) return null;
  const r = state.data;
  // Always compute the share link from the current window so the URL matches
  // what the user sees in the address bar (prod domain vs preview URL). The
  // backend's `link` field is a safe fallback but may reflect the internal
  // ingress hostname when the app is behind a reverse proxy.
  const shareLink = `${window.location.origin}/register?ref=${r.code}`;
  const remaining = Math.max(0, r.qualifying_cap - r.qualifying_count);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually.");
    }
  };

  const share = async () => {
    const shareData = {
      title: "Join me on RecipeFiler",
      text: "I'm using RecipeFiler to save all my family recipes — join me with my code and I'll get bonus recipe slots when you add your first one!",
      url: shareLink,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      copy(shareLink, "Link");
    }
  };

  return (
    <div className="bg-gradient-to-br from-rf-amber/25 to-rf-cream-alt rounded-3xl p-6 border border-rf-amber/40" data-testid="refer-friends-card">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <p className="overline mb-1">Refer friends, earn recipes</p>
          <h3 className="font-display font-black text-2xl text-rf-ink leading-tight">
            +5 free recipes<br />for every friend who joins.
          </h3>
        </div>
        <div className="flex gap-3 text-center">
          <div>
            <p className="text-3xl font-display font-black text-rf-orange leading-none" data-testid="refer-signups">{r.total_signups}</p>
            <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mt-1">Signups</p>
          </div>
          <div>
            <p className="text-3xl font-display font-black text-rf-orange leading-none" data-testid="refer-qualifying">{r.qualifying_count}</p>
            <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mt-1">Qualifying</p>
          </div>
          <div>
            <p className="text-3xl font-display font-black text-rf-orange leading-none" data-testid="refer-bonus">+{r.bonus_recipes}</p>
            <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mt-1">Bonus slots</p>
          </div>
        </div>
      </div>

      <p className="text-sm text-rf-ink-soft mb-4">
        <Users size={14} weight="bold" className="inline mr-1 -mt-0.5" />
        {r.cap_reached ? (
          <>You&apos;ve maxed out the bonus (<b>{r.bonus_cap} recipes</b>) — anyone new you refer still counts on the leaderboard.</>
        ) : (
          <>Share your link. When someone signs up and adds their first recipe, you both win. You&apos;ll unlock up to <b>{r.bonus_cap} bonus recipe slots</b> — <b>{remaining} qualifying referrals remaining</b>.</>
        )}
      </p>

      {/* Personal link */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 p-3 mb-3 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mb-0.5">Your link</p>
          <p className="text-sm font-mono truncate text-rf-ink" data-testid="refer-link">{shareLink}</p>
        </div>
        <button
          type="button"
          data-testid="refer-copy-link"
          onClick={() => copy(shareLink, "Link")}
          className="btn-outline-ink !py-2 !px-3 text-xs"
          aria-label="Copy link"
        >
          <Copy size={14} weight="bold" /> Copy
        </button>
      </div>

      {/* Code (for people who share verbally / by SMS) */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 p-3 mb-4 flex items-center gap-2">
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mb-0.5">Your code</p>
          <p className="text-lg font-mono font-black tracking-widest text-rf-ink" data-testid="refer-code">{r.code}</p>
        </div>
        <button
          type="button"
          data-testid="refer-copy-code"
          onClick={() => copy(r.code, "Code")}
          className="btn-outline-ink !py-2 !px-3 text-xs"
          aria-label="Copy code"
        >
          <Copy size={14} weight="bold" /> Copy
        </button>
      </div>

      <button
        type="button"
        data-testid="refer-share-btn"
        onClick={share}
        className="btn-amber w-full inline-flex items-center justify-center gap-2"
      >
        <ShareNetwork size={16} weight="bold" /> Share your link
      </button>

      {r.qualifying_count > 0 && (
        <details className="mt-4">
          <summary data-testid="refer-history-toggle" className="cursor-pointer text-sm font-bold text-rf-ink">See who&apos;s joined</summary>
          <ul className="mt-3 space-y-1 text-sm">
            {r.referrals.map((row) => (
              <li key={row.referral_id} className="flex justify-between gap-2 border-b border-rf-ink/5 py-1 last:border-b-0">
                <span className="truncate text-rf-ink-soft">{row.referred_email}</span>
                <span className={`text-xs font-bold shrink-0 ${row.bonus_granted ? "text-emerald-700" : "text-rf-ink-soft"}`}>
                  {row.bonus_granted
                    ? <><Gift size={12} weight="bold" className="inline mr-1 -mt-0.5" /> +5 earned</>
                    : (row.qualified_at ? "Cap reached" : "Pending first recipe")}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
